import type { Vec3 } from '../utils/math';

/** Axis-aligned box in vehicle space: origin = ground centre of the tracks, +z = front, +x = right. */
export interface VehicleBox {
  readonly center: Vec3;
  readonly size: Vec3;
}

/**
 * Dozer set piece (SEG_DOZER_60M, user 2026-10-02: "darauf springen … Erde vor sich herschieben … dass man eine
 * Belohnung bekommt"). Jump onto the parked dozer, drive it ≈ 40 m through heaps of earth past concrete obstacles
 * and push the earth into the pit at the end – rewards per heap, for the filled pit and for a perfect run.
 * Lengths in meters, times in seconds, angles in degrees.
 */
export interface DozerConfig {
  /**
   * VEH_DOZER_A as published (tools/blender/assets/VEH_05_Catapillar.py, GLB checked by tests/unit/dozer.test.ts):
   * game space, origin = ground centre of the track footprint, +z = front.
   */
  readonly vehicle: {
    readonly assetId: string;
    /** Obstacle id of the parked dozer (OBS_DOZER_A) – its colliders are `colliders`. */
    readonly obstacleId: string;
    /** COLLIDER_BODY and COLLIDER_BLADE of the collision export: what a runner on foot runs into. */
    readonly colliders: readonly VehicleBox[];
    /** VEH_DOZER_A_SOCKET_SEAT: the rider's hips (and the root of ANIM_Drive). */
    readonly seat: Vec3;
    /** Top of the rubber tracks – the lowest part a jump lands on (track, fender, seat). */
    readonly trackTop: number;
    /** UV v = 1 along the belt corresponds to this many meters (VEH_05_Catapillar.py: TRACK_TILE = 4 lugs × 0.10 m). */
    readonly trackTileLength: number;
  };
  /** Driving: the run's lane logic with a heavier machine. */
  readonly drive: {
    /**
     * Forward speed = run speed × this. 0.85: the heavy machine is noticeably slower than the runner (15.3 m/s at top
     * speed) – one lane change between two rows of the layout needs reaction + steering = 0.3 + 0.36 s = 10.1 m.
     */
    readonly speedFactor: number;
    /** Seconds per lane while steering: 1.5 × the run's 0.24 s – the dozer swings over a little more slowly. */
    readonly laneChangeDuration: number;
    /**
     * Gameplay box while driving (what obstacles, heaps and bottles meet). 2.1 m wide: a little narrower than the
     * 2.34 m blade, like the forgiving container collider – a lane-centred obstacle in the next lane never scrapes it.
     * 2.45 m high: seat 1.48 m + the seated rider (≈ 0.95 m) – bottles arcing over a heap stay collectable.
     */
    readonly body: { readonly width: number; readonly height: number };
  };
  /** The pit at the end of the module. */
  readonly pit: {
    /**
     * Heaps pushed (pile in front of the blade) needed to fill the pit: 2 – every layout places 3 heaps, so a
     * driver may miss one heap (or swerve round one obstacle the long way) and still fill the pit; "perfect" needs all.
     */
    readonly fillThreshold: number;
    /** Too little earth: the blade stops this far before the edge – the rider is catapulted over the pit. */
    readonly stopMargin: number;
    /** The catapult lands the rider's centre at least this far past the far edge (one body length + margin). */
    readonly landingMargin: number;
  };
  /**
   * End of the ride over the filled pit: once the seat is past the far edge the rider hops off forward and lands at
   * the latest `landBeforeEnd` m before the end of the module (at the run speed; never higher than a normal jump) –
   * the landing stays on the module's own run-out, the next module is met running like after a jump over the pit.
   */
  readonly finish: { readonly landBeforeEnd: number };
  /** Thrown off by a hit: the rider flies over the blade and lands past the obstacle that stopped the machine. */
  readonly throwOff: {
    /** Clearance ahead of the blade: the deepest obstacle of the layouts (concrete pipe 1.12 m) + 0.08 m. */
    readonly clearance: number;
    /** Vertical speed of the hop off the seat (m/s) – a short arc forward, landing within ≈ 0.6 s. */
    readonly launchSpeed: number;
  };
  /** An abandoned dozer (jumped off, thrown off, end of the ride) brakes to a stop – never into the pit. */
  readonly brakeDeceleration: number;
  /** Rewards (ScoreManager): bottles per pushed heap, for the filled pit, extra lives for a perfect run. */
  readonly rewards: {
    readonly bottlesPerHeap: number;
    readonly pitFilledBottles: number;
    /** All heaps of the layout pushed and the pit filled – through score:extraLife (the drink break follows). */
    readonly perfectExtraLives: number;
  };
  /** Obstacle id of the heaps (OBS_DIRT_MOUND_A, a jump for runners on foot) – the blade pushes them. */
  readonly heapObstacleId: string;
  /** Visual and audible feedback (DozerPresenter, PlayerView, AudioManager). */
  readonly presentation: DozerPresentationConfig;
}

export interface DozerPresentationConfig {
  /** Blade angle about the push-arm trunnion (positive lifts): resting parked, carried driving, lowered pushing. */
  readonly blade: {
    readonly parkedDegrees: number;
    readonly driveDegrees: number;
    readonly pushDegrees: number;
    /** Extra dip when a heap is hit, fading over `dipSeconds`. */
    readonly dipDegrees: number;
    readonly dipSeconds: number;
    /** Exponential follow rate (1/s). */
    readonly rate: number;
  };
  /** Turn of the body into a lane change (heading follows the lateral speed, capped) and its follow rate (1/s). */
  readonly steerYawDegrees: number;
  readonly steerYawRate: number;
  /** Body rumble of the running engine: vertical shake (m) at idle and while driving, frequency (Hz). */
  readonly rumble: { readonly idle: number; readonly drive: number; readonly hz: number };
  /** Earth pile in front of the blade (OBS_DIRT_MOUND_A visual): size per pushed heap, growth time. */
  readonly pile: {
    readonly assetId: string;
    /** Scale of the heap visual with one heap pushed, and added per further heap (capped at `maxScale`). */
    readonly firstScale: number;
    readonly perHeapScale: number;
    readonly maxScale: number;
    readonly growSeconds: number;
    /** The pile slides into the pit over this time. */
    readonly dumpSeconds: number;
  };
  /**
   * Earth fill of the pit (box flush with the road, the dumped pile flattened on top): colour, how long it takes to
   * rise, and the height of the flat mound left on top (visual only – the walkable fill is flush with the road).
   */
  readonly fill: { readonly color: string; readonly riseSeconds: number; readonly moundHeight: number };
  /** Bottles springing out of a pushed heap and flying into the rider (visual – the bottles count at once). */
  readonly fountain: { readonly assetId: string; readonly count: number; readonly seconds: number; readonly height: number; readonly spread: number };
  /** Exhaust smoke at VEH_DOZER_A_SOCKET_EXHAUST: puffs per second idle / driving. */
  readonly exhaust: {
    readonly capacity: number;
    readonly idleRate: number;
    readonly driveRate: number;
    readonly size: { readonly min: number; readonly max: number; readonly growth: number };
    readonly lifetime: { readonly min: number; readonly max: number };
    readonly speed: { readonly min: number; readonly max: number };
    readonly rise: number;
    readonly color: readonly [number, number, number, number];
    readonly colorFade: readonly [number, number, number, number];
  };
  /** Earth spray: a burst when a heap is hit, a steady trickle at the blade while the pile is pushed. */
  readonly spray: {
    readonly capacity: number;
    readonly burst: number;
    readonly pushRate: number;
    readonly size: { readonly min: number; readonly max: number; readonly growth: number };
    readonly lifetime: { readonly min: number; readonly max: number };
    readonly speed: { readonly min: number; readonly max: number };
    readonly gravity: number;
    readonly color: readonly [number, number, number, number];
    readonly colorFade: readonly [number, number, number, number];
  };
  /** The rider on the seat (PlayerView). */
  readonly rider: {
    /**
     * Without ANIM_Drive in the character GLB the rider stands on the platform in front of the seat (Idle):
     * offset from VEH_DOZER_A_SOCKET_SEAT (vehicle space, m).
     */
    readonly fallbackOffset: Vec3;
    /** Blend of the body into / out of the seat (s) and the height of its little arc (m). */
    readonly mountSeconds: number;
    readonly mountArc: number;
    readonly dismountSeconds: number;
    readonly throwSeconds: number;
    readonly throwArc: number;
    /** Lean into steering (deg per m/s lateral speed, capped) – the clip has none. */
    readonly leanDegreesPerLateralSpeed: number;
    readonly maxLeanDegrees: number;
  };
  /** Camera shake (trauma 0..1): heap pushed, earth into the pit, hard stop at the pit edge. */
  readonly trauma: { readonly heap: number; readonly fill: number; readonly stop: number };
  /** Toasts: seconds on screen. */
  readonly toastSeconds: number;
  /** Engine sound: chugs per second idle / driving, heard up to `range` m away (loudness fades to `minLoudness`). */
  readonly engine: { readonly idleRate: number; readonly driveRate: number; readonly range: number; readonly minLoudness: number };
}

export const DOZER_CONFIG: DozerConfig = {
  vehicle: {
    assetId: 'VEH_DOZER_A',
    obstacleId: 'OBS_DOZER_A',
    colliders: [
      { center: { x: 0, y: 0.875, z: -0.2 }, size: { x: 1.7, y: 1.75, z: 3.45 } },
      { center: { x: 0, y: 0.46, z: 1.82 }, size: { x: 2.34, y: 0.92, z: 0.55 } },
    ],
    seat: { x: 0, y: 1.48, z: -1.02 },
    trackTop: 0.79,
    trackTileLength: 0.4,
  },
  drive: {
    speedFactor: 0.85,
    laneChangeDuration: 0.36,
    body: { width: 2.1, height: 2.45 },
  },
  pit: {
    fillThreshold: 2,
    // one fixed step of the dozer at top speed (15.3 m/s / 60 Hz = 0.26 m) – the blade never pokes over the edge
    stopMargin: 0.3,
    landingMargin: 1,
  },
  // 1 m: the runner's centre lands at least one step (half a body + margin) before the module border
  finish: { landBeforeEnd: 1 },
  throwOff: { clearance: 1.2, launchSpeed: 3 },
  // A crawler stops hard; behind the rider (the camera passes over it).
  brakeDeceleration: 12,
  rewards: { bottlesPerHeap: 10, pitFilledBottles: 50, perfectExtraLives: 1 },
  heapObstacleId: 'OBS_DIRT_MOUND_A',
  presentation: {
    blade: { parkedDegrees: 0, driveDegrees: 7, pushDegrees: 2, dipDegrees: 3, dipSeconds: 0.35, rate: 6 },
    steerYawDegrees: 9,
    // ≈ 0.12 s to follow: the swing reads within one lane change (0.36 s), without jitter between fixed steps
    steerYawRate: 8,
    rumble: { idle: 0.006, drive: 0.012, hz: 23 },
    pile: { assetId: 'OBS_DIRT_MOUND_A', firstScale: 0.55, perHeapScale: 0.2, maxScale: 1.05, growSeconds: 0.35, dumpSeconds: 0.6 },
    fill: { color: '#6e5a43', riseSeconds: 0.6, moundHeight: 0.18 },
    fountain: { assetId: 'COL_BEER_BOTTLE_A', count: 5, seconds: 0.75, height: 2.2, spread: 1.4 },
    exhaust: {
      capacity: 90,
      idleRate: 7,
      driveRate: 22,
      size: { min: 0.18, max: 0.32, growth: 3 },
      lifetime: { min: 0.7, max: 1.3 },
      speed: { min: 0.6, max: 1.2 },
      rise: 0.9,
      color: [0.2, 0.2, 0.21, 0.45],
      colorFade: [0.45, 0.45, 0.46, 0],
    },
    // Clods, not a cloud: small, falling fast and gone within ≈ half a second – a long-lived spray lay on the road
    // behind the machine like a brown smear the camera drove through (browser check 2026-10-02).
    spray: {
      capacity: 160,
      burst: 35,
      pushRate: 18,
      size: { min: 0.07, max: 0.2, growth: 1.2 },
      lifetime: { min: 0.3, max: 0.6 },
      speed: { min: 2.2, max: 4.6 },
      gravity: -14,
      color: [0.42, 0.33, 0.24, 0.95],
      colorFade: [0.5, 0.42, 0.33, 0],
    },
    rider: {
      // feet on the operator platform (≈ 0.98 m) between seat and hood, hands at the levers
      fallbackOffset: { x: 0, y: -0.5, z: 0.45 },
      mountSeconds: 0.22,
      mountArc: 0.35,
      dismountSeconds: 0.12,
      throwSeconds: 0.35,
      throwArc: 0.8,
      leanDegreesPerLateralSpeed: 1.6,
      maxLeanDegrees: 7,
    },
    trauma: { heap: 0.22, fill: 0.4, stop: 0.5 },
    toastSeconds: 1.8,
    engine: { idleRate: 7, driveRate: 12, range: 45, minLoudness: 0.15 },
  },
};
